// Sitar / "lofi Bollywood" (docs/AUDIO_DESIGN.md §12): a harmonium holds simple triads and sus chords over a tanpura
// drone (Sa and Pa, buzzing on its threaded bridge), a tabla plays keherwa, a 3-3-2 film lilt or teentaal's first half
// (a soft lofi kick and snare under it from balanced up), and a sitar sings the melody with meend (glides), grace notes in
// a raga (Yaman or Khamaj in major keys, Kafi or Bhairavi in minor ones) and its sympathetic strings ringing after it.
// Dusty and warm: more tape dust than jazzhop, the drone always there. Instruments are in sitar-voice.js and sitar-band.js.
import { rng } from '../../engine/core.js';
import { QUALITIES, bassNote, scaleNotes, trackProgs } from '../harmony.js';
import { createSitar } from './sitar-voice.js';
import { createHarmonium, createTabla, createSoftBass } from './sitar-band.js';

const mod12 = n => ((n % 12) + 12) % 12;
const E_IX = { chill: 0, balanced: 1, upbeat: 2 };

/* ---------------- harmonium voicings: one hand, 3 or 4 notes ---------------- */
// A harmonium is played with the right hand (the left pumps the bellows): a close triad in any inversion, or a triad with
// its lowest note again an octave up (span 12); an open fifth as R-5-R or 5-R-5. Tones: sitM major, sitm minor, sit5 the
// fifth, sit2 / sit4 sus. The engine's final tonic (maj9 / 69 / m9) becomes the triad of the key, or an open fifth on
// plan.sitar.final5 tracks (the tanpura's own chord); anything else is reduced to its triad. Kept: lowest MIDI 48–64, top
// 57–69, neighbours ≥ a whole step apart (≥ a minor 3rd under MIDI 52).
const TONES = { sitM: [0, 4, 7], sitm: [0, 3, 7], sit5: [0, 7], sit2: [0, 2, 7], sit4: [0, 5, 7] };
const tonesOf = (c, plan) => {
  if (TONES[c.q]) return TONES[c.q];
  if (c.final) return plan.sitar && plan.sitar.final5 ? TONES.sit5 : plan.key.mode === 'minor' ? TONES.sitm : TONES.sitM;
  const iv = (QUALITIES[c.q] || [0, 4, 7]).map(mod12);
  return iv.includes(4) ? TONES.sitM : iv.includes(3) ? TONES.sitm : TONES.sit5;
};
const CANDS = new Map();                                    // `${root}:${tones}` → [[midi, …]]
function candidates(root, tones) {
  const key = `${root}:${tones}`;
  if (CANDS.has(key)) return CANDS.get(key);
  const pcs = tones.map(i => mod12(root + i)), out = [], n = pcs.length;
  const shapes = n === 2 ? [[0, 1, 0], [1, 0, 1]] : [0, 1, 2].map(k => [k, (k + 1) % 3, (k + 2) % 3]);
  for (const sh of shapes) for (let u0 = 48 + mod12(pcs[sh[0]] - 48); u0 <= 64; u0 += 12) {
    const u = [u0];
    for (let k = 1; k < sh.length; k++) { let m = u[k - 1] + 1; m += mod12(pcs[sh[k]] - m); u.push(m); }
    const four = n === 3 ? [...u, u[0] + 12] : null;
    for (const v of four ? [u, four] : [u]) {
      const top = v[v.length - 1];
      if (top < 57 || top > 69 || v.some((m, i) => i && m - v[i - 1] < (v[i - 1] < 52 ? 3 : 2))) continue;
      out.push(v);
    }
  }
  CANDS.set(key, out);
  return out;
}
/** movement between voicings (different sizes: each note to the nearest of the other, +1) */
function move(a, b) {
  if (a.length === b.length) return a.reduce((s, x, i) => s + Math.abs(x - b[i]), 0);
  const near = (x, c) => Math.min(...c.map(y => Math.abs(x - y)));
  return (a.reduce((s, x) => s + near(x, b), 0) + b.reduce((s, y) => s + near(y, a), 0)) / 2 + 1;
}
/** Cost: Σ|Δ| + 2·max(0, |Δtop| − 3) + .35·|mean − 58|, −.6 for 4 notes on a chord of a bar or more (+.4 on a shorter
 *  one); the first chord .6·|mean − (58 + register)| + .3·|top − (63 + register)| instead of the motion terms, so tracks
 *  don't all open on the same notes. The same chord keeps its voicing (re-pressed cells and 2-bar chords). */
export function harmoniumVoicing(sp, prev, c, plan) {
  const root = mod12(plan.key.tonic + c.root), tones = tonesOf(c, plan), reg = plan.register || 0;
  const want = new Set(tones.map(i => mod12(root + i)));
  if (prev && prev.length >= 3) { const have = new Set(prev.map(mod12)); if (have.size === want.size && [...want].every(p => have.has(p))) return prev; }
  let best = null, bc = Infinity;
  for (const v of candidates(root, tones)) {
    const top = v[v.length - 1], mean = v.reduce((s, m) => s + m, 0) / v.length;
    let k = v.length === 4 ? (c.beats >= 4 ? -0.6 : 0.4) : 0;
    if (prev) k += move(v, prev) + 2 * Math.max(0, Math.abs(top - prev[prev.length - 1]) - 3) + 0.35 * Math.abs(mean - 58);
    else k += 0.6 * Math.abs(mean - 58 - reg) + 0.3 * Math.abs(top - 63 - reg);
    if (k < bc) { bc = k; best = v; }
  }
  return best || tones.map(i => 55 + mod12(root + i - 55)).sort((a, b) => a - b);
}

/* ---------------- bass: a soft root on the chord changes, resting often ---------------- */
// Kick mode only (the intro, break and outro take the default roots): a root on each chord's first bar (its 2nd bar
// p .5), the whole bar silent p .3 / .18 / .1 by energy (the tanpura holds the low Sa anyway), and a pickup a tone
// above or below the next root on step 14 (p .15 / .25 / .35).
const REST = [0.3, 0.18, 0.1], PICKUP = [0.15, 0.25, 0.35];
function bassLine({ plan, st, mode, chords, nextChords, stop }) {
  if (mode !== 'kick') return null;
  const E = E_IX[plan.vibe.energy] ?? 1, r = st.rS, pc = c => mod12(plan.key.tonic + c.root), out = [], end = stop ?? 16;
  if (r() < REST[E]) return out;
  for (const c of chords) {
    const s = c.start * 4, root = bassNote(pc(c), st.prevBass);
    if (s >= end || (c.onset === false && r() < 0.5)) continue;
    out.push({ s, midi: root, vel: c.onset === false ? 0.72 : 0.84, dur: Math.max(0.5, Math.min(c.beats * 4, end - s) * plan.bass.factor) });
    st.prevBass = root;
  }
  const last = chords[chords.length - 1], nx = nextChords && nextChords[0];
  if (end > 14 && nx && pc(nx) !== pc(last) && r() < PICKUP[E]) {
    const tg = bassNote(pc(nx), st.prevBass), a = r() < 0.5 ? tg - 2 : tg + 2;
    for (const n of out) n.dur = Math.max(0.5, Math.min(n.dur, 13.6 - n.s));
    out.push({ s: 14, midi: a < 33 ? a + 12 : a, vel: 0.66, dur: 1.6, slide: r() < 0.3 });
  }
  return out;
}

/* ---------------- melody: a gat-like motif, strokes and meends ---------------- */
// [steps, gaps] over 2 bars: 'l' = legato into the next note (the sitar pulls the string: a meend), '.' = a new stroke
// of the mizrab. Meends move 1 scale step (p .6) or 2; strokes 1–2 steps (leaning one way) or a leap of 3 that turns
// back; the contour stays within ±4 steps; the last note lasts 6–10 steps (the pulled vibrato). The engine picks the
// notes (the key's pentatonic, chord tones on beats 1 and 3); the sitar adds the raga's other notes as ornaments.
const GATS = [
  [[0, 2, 4, 8], '.l.'], [[0, 3, 4, 8, 12], '..l.'], [[0, 2, 3, 6, 8], '.l..'], [[2, 4, 6, 8, 14], '..l.'],
  [[0, 4, 6, 7, 10], '.l.l'], [[0, 2, 4, 6, 8, 12], '...l.'], [[0, 6, 8, 12, 16], '.l.l'], [[0, 3, 6, 8, 11, 12], '..l.l'],
];
function ragaMotif(r, key) {
  const [steps, gaps] = GATS[Math.floor(r() * GATS.length)], n = steps.length, last = 6 + Math.floor(r() * 5);
  const durs = steps.map((s, i) => (i === n - 1 ? last : gaps[i] === 'l' ? Math.min(6, steps[i + 1] - s) : Math.max(1, Math.min(6, steps[i + 1] - s) - 2)));
  const contour = [0];
  let dir = r() < 0.55 ? 1 : -1, back = 0;
  for (let i = 1; i < n; i++) {
    let mv;
    if (back) { mv = back; back = 0; }
    else if (gaps[i - 1] === 'l') mv = (r() < 0.6 ? 1 : 2) * (r() < 0.6 ? dir : -dir);
    else if (r() < 0.82) mv = (1 + Math.floor(r() * 2)) * (r() < 0.7 ? dir : -dir);
    else { mv = 3 * dir; back = -dir; }
    let v = contour[i - 1] + mv;
    if (Math.abs(v) > 4) { v = contour[i - 1] - mv; dir = -dir; }
    contour.push(v);
  }
  const vel = steps.map((s, i) => (i === 0 ? 0.8 : gaps[i - 1] === 'l' ? 0.7 : +(0.64 + 0.1 * r()).toFixed(3)));
  return { steps, durs, contour, vel, mode: key.mode };
}

/* ---------------- comping: harmonium cells [step, length, velocity] ---------------- */
const COMPS = {
  hold: [[0, 16]],                                              // held, the bellows breathing
  two: [[0, 8], [8, 8, 0.82]],                                   // pressed again on beat 3
  filmi: [[0, 6], [6, 6, 0.78], [12, 4, 0.86]],                  // 3-3-2
  chhand: [[0, 3], [4, 3, 0.7], [8, 3, 0.86], [12, 3, 0.7]],     // a bhajan's detached quarters
  breath: [[0, 14], [14, 2, 0.62]],                              // a quick re-press before the bar line
};

/* ---------------- tabla ---------------- */
// H dayan na (x) / tin (o) · R ti (x) / te (g) · T bayan ge (x; g soft) · W ka (bayan closed) · S lofi snare (x) / te (g)
// · K lofi kick · X ghungroo. Matras are 8ths (2 steps): keherwa's 8 fill one bar, teentaal's 16 two.
const GRIDS = {
  //          0123456789abcdef
  keherwa: { K: 'x.........x.....', S: '....x.......x...', H: 'x...x...x...o.x.', T: 'x.x.........x...', R: '......x.........', W: '..........x.....' },   // Dha Ge Na Ti | Na Ka Dhi Na
  chalti: { K: 'x.........x.....', S: '...gx.......xg..', H: 'x...x...x...o.x.', T: 'x.x.....s...x...', R: '......xg.......g', W: '..........x.....' },   // keherwa rolling with te-te
  teen: { K: 'x.......x.......', S: '....x.......x...', H: 'x.o.o.x.x.o.o.x.', T: 'x.....x.x.....x.', X: '..s...s...s...s.' },   // Dha Dhin Dhin Dha ×2
  filmi: { K: 'x.....x.........', S: '........x.......', H: 'x..x..x...x.o...', T: 'x.....x.....x...', R: '..............xg', W: '........x.......' },   // 3-3-2, a film dholak's lilt
  sparse: { H: 'x.......o...x...', T: 'x.....x.........', X: '....s.......s...', R: '..............g.' },   // chill: Dha … Ge · Tin … Na
  thah: { H: 'x...x...x...o.x.', T: 'x.x.........x...', R: '......x.........', W: '..........x.....' },   // chill: keherwa, the tabla alone
  lilt: { H: 'x..x..x...x.o...', T: 'x.....x.....x...', R: '..............xg', W: '........x.......', X: '....s.......s...' },   // chill: 3-3-2 alone
  break: { H: 'x.......o.......', X: 'x...s...x...s...' },
};
const GROOVES = {
  chill: { sparse: 1.3, thah: 1.2, lilt: 1 },
  slow: { keherwa: 1.3, filmi: 1, thah: 0.5, sparse: 0.3 },
  fast: { keherwa: 1.2, filmi: 1, chalti: 0.6, teen: 0.5 },
  upbeat: { chalti: 1.2, keherwa: 1, teen: 0.9, filmi: 0.8 },
};

export default {
  id: 'sitar', name: 'Sitar', desc: 'Sitar and harmonium over a tanpura drone and a tabla groove',
  //            chill                          balanced                       upbeat
  vary: 'half',
  bridge: false,
  energy: {
    stretch:    [0.6,                          0.45,                          0.3],
    bpm:        [[60, 70],                     [66, 78],                      [74, 86]],
    swing:      [[55, 60],                     [53, 58],                      [52, 56]],
    snareLag:   [[0.008, 0.016],               [0.005, 0.012],                [0.003, 0.008]],
    groove:     [[GROOVES.chill, GROOVES.chill], [GROOVES.slow, GROOVES.fast], [GROOVES.upbeat, GROOVES.upbeat]],
    drumLevel:  [0.72,                         0.82,                          0.88],
    kitLp:      [[4200, 6000],                 [5000, 7200],                  [5800, 8200]],
    epLp:       [[1400, 1900],                 [1600, 2300],                  [1900, 2700]],     // the harmonium bus LP
    tremDepth:  [[0.08, 0.14],                 [0.06, 0.12],                  [0.05, 0.1]],      // the bellows (± of the level)
    strum:      [[0.012, 0.024],               [0.01, 0.02],                  [0.008, 0.016]],   // one hand's spread
    epVel:      [[0.5, 0.6],                   [0.54, 0.64],                  [0.56, 0.68]],
    comp: [
      { hold: [1, 1.5], two: [0.3, 0.6], filmi: [0, 0.2], chhand: [0, 0], breath: [0.2, 0.5] },
      { hold: [0.5, 0.9], two: [0.4, 0.8], filmi: [0.3, 0.7], chhand: [0, 0.2], breath: [0.2, 0.4] },
      { hold: [0.2, 0.4], two: [0.4, 0.7], filmi: [0.6, 1], chhand: [0.3, 0.7], breath: [0.1, 0.3] },
    ],
    push:       [0.04,                         0.08,                          0.14],
    legato:     [0.85,                         0.75,                          0.6],
    lead:       [0.82,                         0.88,                          0.94],
    softLead:   [0.7,                          0.6,                           0.5],              // the warm sitar over the bright one
    tone:       [[3800, 5600],                 [4600, 6800],                  [5400, 7800]],     // dusty: the reeds and the buzz are bright
    wowCents:   [[6, 11],                      [5, 9],                        [4, 8]],
    dust:       [[1, 1.6],                     [0.8, 1.4],                    [0.6, 1.2]],
    introHats:  [0.3,                          0.45,                          0.6],
    leadFrom:   [1,                            1,                             1],               // the sitar from the first A
    hatsFirst:  [0.4,                          0.3,                           0.2],
    breakDrums: [0.3,                          0.5,                           0.75],
    breakLead:  [0.4,                          0.5,                           0.6],
    orn:        [[0.12, 0.22],                 [0.15, 0.26],                  [0.18, 0.3]],      // odds of a kan per sitar stroke
    meend:      [[0.3, 0.45],                  [0.25, 0.4],                   [0.2, 0.35]],      // odds of a meend from below
    swoop:      [[0.35, 0.55],                 [0.3, 0.5],                    [0.25, 0.45]],     // odds a bayan ge swoops
    tanpura:    [[0.95, 1.15],                 [0.85, 1.05],                  [0.75, 0.95]],     // the drone's level
    // development (arrange.js BY_ENERGY): no lift (the tanpura's drone); taans on the gat; the alap: the sitar first
    lift:       [0, 0, 0],
    solo:       [0.3, 0.35, 0.4],
    intro:      [{ keys: 0.35, beat: 0.1, groove: 0.05, lead: 0.5, cold: 0 }, { keys: 0.35, beat: 0.1, groove: 0.05, lead: 0.5, cold: 0 }, { keys: 0.35, beat: 0.1, groove: 0.05, lead: 0.5, cold: 0 }],
  },
  qualities: { sitM: [0, 4, 7], sitm: [0, 3, 7], sit5: [0, 7, 12], sit2: [0, 2, 7], sit4: [0, 5, 7] },
  progressions: {
    major: {
      y1: [[0, 'sitM', 4], [2, 'sitM', 4], [9, 'sitm', 4], [0, 'sitM', 4]],                        // I · II · vi · I (Yaman)
      y2: [[0, 'sitM', 4], [0, 'sit2', 4], [2, 'sitM', 4], [2, 'sitM', 4]],                        // I · Isus2 · II · II
      y3: [[0, 'sitM', 4], [2, 'sitM', 4], [7, 'sitM', 4], [0, 'sitM', 4]],                        // I · II · V · I
      y4: [[9, 'sitm', 4], [7, 'sitM', 4], [2, 'sitM', 2], [0, 'sit4', 2], [0, 'sitM', 4]],        // vi · V · II|Isus4 · I
      k1: [[0, 'sitM', 4], [10, 'sitM', 4], [5, 'sitM', 4], [0, 'sitM', 4]],                       // I · ♭VII · IV · I (Khamaj)
      k2: [[0, 'sitM', 4], [5, 'sitM', 4], [7, 'sitm', 4], [0, 'sitM', 4]],                        // I · IV · v · I
      k3: [[0, 'sit4', 4], [0, 'sitM', 4], [10, 'sitM', 4], [0, 'sitM', 4]],                       // Isus4 · I · ♭VII · I
      k4: [[0, 'sitM', 4], [10, 'sitM', 4], [0, 'sitM', 4], [7, 'sitm', 2], [5, 'sitM', 2]],       // I · ♭VII · I · v|IV
    },
    minor: {
      f1: [[0, 'sitm', 4], [5, 'sitM', 4], [0, 'sitm', 4], [5, 'sitM', 4]],                        // i · IV · i · IV (Kafi)
      f2: [[0, 'sitm', 4], [10, 'sitM', 4], [5, 'sitM', 4], [0, 'sitm', 4]],                       // i · ♭VII · IV · i
      f3: [[0, 'sitm', 4], [3, 'sitM', 4], [10, 'sitM', 4], [5, 'sitM', 4]],                       // i · ♭III · ♭VII · IV
      f4: [[0, 'sitm', 4], [7, 'sitm', 4], [10, 'sitM', 4], [0, 'sit2', 4]],                       // i · v · ♭VII · isus2
      b1: [[0, 'sitm', 4], [1, 'sitM', 2], [0, 'sitm', 2], [0, 'sitm', 4], [10, 'sitm', 4]],       // i · ♭II|i · i · ♭vii (Bhairavi)
      b2: [[0, 'sitm', 4], [5, 'sitm', 4], [1, 'sitM', 2], [0, 'sitm', 2], [0, 'sitm', 4]],        // i · iv · ♭II|i · i
      b3: [[0, 'sitm', 4], [8, 'sitM', 4], [10, 'sitm', 4], [0, 'sitm', 4]],                       // i · ♭VI · ♭vii · i
      b4: [[8, 'sitM', 4], [1, 'sitM', 4], [0, 'sitm', 4], [0, 'sit5', 4]],                        // ♭VI · ♭II · i · i5
    },
  },
  // the bar that ends progB when it repeats progA: Isus4|I in major, ♭VII|i in minor (drone-friendly, no ii–V)
  turnarounds: { major: [[0, 'sit4', 2], [0, 'sitM', 2]], minor: [[10, 'sitM', 2], [0, 'sitm', 2]] },
  grids: GRIDS,
  fills: {
    tirakita: { roll: [[12, 0.62], [13, 0.4], [14, 0.5], [15, 0.66]], voice: 'rim' },               // ti-ra-ki-ta into the sam
    ge: { kicks: [13, 15], voice: 'tom' },                                                            // bayan pickups
    tin: { open: 14 },                                                                                // a ringing tin
    stop: { stop: 12 },                                                                               // the tanpura rings on
  },
  comps: COMPS,
  keysLevel: 1.15,                                               // the harmonium and the drone are most of the band

  plan(plan, { r, E }) {
    const rr = (a, b, d = 3) => +(a + r() * (b - a)).toFixed(d);
    const minor = plan.key.mode === 'minor', pcsOf = cs => new Set(cs.flatMap(c => QUALITIES[c.q].map(i => mod12(c.root + i))));
    const all = pcsOf(trackProgs(plan).flatMap(p => p.chords)), A = pcsOf(plan.progA.chords), coin = r();
    // the raga follows progA's harmony: Yaman (♯4, 7) or Khamaj (4, ♭7) in major, Bhairavi (♭2, ♭6) or Kafi (2, 6) in
    // minor; progB decides a tie, a coin a track with neither (the sitar's ornaments take a chord's own note where it has one)
    const [x, y] = minor ? [[1, 8], [2, 9]] : [[6, 11], [5, 10]];
    const score = s => x.filter(p => s.has(p)).length - y.filter(p => s.has(p)).length, k = score(A) || score(all) || (coin < 0.5 ? 1 : -1);
    plan.sitar = {
      raga: minor ? (k > 0 ? 'bhairavi' : 'kafi') : (k > 0 ? 'yaman' : 'khamaj'),
      orn: rr(...E.orn), meend: rr(...E.meend), tarab: rr(0.8, 1.25, 2), final5: r() < 0.45,
    };
    plan.harm = { det: rr(5, 10, 1), bellows: rr(0.15, 0.27), depth: plan.ep.tremDepth, nasal: rr(1.5, 3.5, 1), atk: rr(0.018, 0.034) };
    plan.tanpura = { level: rr(...E.tanpura, 2), offset: rr(0, 1) };
    plan.drums.kit = {
      dayan: 59 + mod12(plan.key.tonic - 59), bayanF: rr(84, 104, 1), swoop: rr(...E.swoop, 2), rise: rr(3, 6, 1),
      naDecay: rr(0.28, 0.4), tinDecay: rr(0.45, 0.65), geDecay: rr(0.3, 0.45), kickF0: rr(100, 118, 1), kickF1: rr(45, 51, 1),
      kickDecay: rr(0.14, 0.19), snareF: rr(1400, 1900, 0), snareDecay: rr(0.08, 0.12), lp: plan.drums.kit.lp,
      sd: +(60 / plan.bpm / 4).toFixed(6), seed: plan.seed % 100003,
    };
    plan.bass.lp = rr(300, 400, 0);
    if (plan.lead) Object.assign(plan.lead, { timbre: plan.lead.timbre === 'soft' ? 'warm' : 'bright', motif: ragaMotif(r, plan.key) });
  },

  voicing: harmoniumVoicing,
  bass: bassLine,
  voices(ac, dry, wet, plan, ctx) {
    const o = { registry: ctx.registry, tag: ctx.tag, t0: ctx.t0 }, sub = () => rng(Math.floor(ctx.r() * 4294967296));
    let ep = null;
    const lead = plan.lead
      ? createSitar(ac, dry, wet, plan, { ...o, stepDur: ctx.stepDur, r: sub(), lite: ctx.cap < 16, chordAt: t => (ep ? ep.pcsAt(t) : null) })
      : null;
    ep = createHarmonium(ac, dry, wet, plan, { ...o, cap: ctx.cap, r: sub(), log: ctx.log, stepDur: ctx.stepDur, onChord: lead ? () => lead.warmStep() : null });
    if (lead) lead.warm(scaleNotes(plan.key, 67, 84), 3);
    return { ep, bass: createSoftBass(ac, dry, plan.bass, o), kit: createTabla(ac, dry, wet, plan.drums.kit, o), lead };
  },
};
